import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Heading } from './common/Catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './common/Catalyst/table';
import { ArrowPathIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { CheckReturn } from './clients/rust-api-client';
import { bankService } from './clients/ApiClients';
import { getUserTypeFromToken } from './utils/TokenUtils';
import { canManageStopPayments, canRecordCheck } from './constants/UserTypes';
import { formatCurrency, formatDate, getBankErrorParts } from './utils/bankHelpers';
import { Button } from './common/Catalyst/button';
import { Badge } from './common/Catalyst/badge';
import { Text } from './common/Catalyst/text';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import CheckDialog from './CheckDialog';
import VoidCheckDialog from './VoidCheckDialog';
import StopPaymentDialog from './StopPaymentDialog';
import { StopPaymentReturn } from './clients/rust-api-client';

interface HoaCheckRegisterProps {
  hoaId: string;
  /**
   * Bumped by the page when a stop is placed or released elsewhere, so
   * the register's Stopped badge follows without owning that list.
   */
  refreshKey?: number;
  /** Called after a stop is placed from a row here, for the stop list. */
  onStopPlaced?: (stop: StopPaymentReturn) => void;
}

interface AlertState {
  type: 'error' | 'success';
  title: string;
  message: string;
}

type BadgeColor = 'lime' | 'red' | 'amber' | 'zinc' | 'sky' | 'purple';

// Positive Pay submission status → badge. `sent` is amber on purpose: the
// bank has accepted the entry but not yet imported it, and an entry that
// stays there is the one thing on this panel worth a person's attention.
const ppColor = (status?: string | null): BadgeColor => {
  switch (status) {
    case 'downloaded':
      return 'lime';
    case 'sent':
      return 'amber';
    case 'failed':
      return 'red';
    case 'pending':
    case 'sending':
      return 'sky';
    default:
      return 'zinc';
  }
};

const ppLabel = (status?: string | null): string => {
  switch (status) {
    case 'downloaded':
      return 'At bank';
    case 'sent':
      return 'Accepted';
    case 'failed':
      return 'Failed';
    case 'sending':
      return 'Sending…';
    case 'pending':
      return 'Pending';
    default:
      return status ?? '—';
  }
};

const glSkipLabel = (reason?: string | null): string => {
  switch (reason) {
    case 'no_period':
      return 'Not posted — no accounting period';
    case 'no_cash_account':
      return 'Not posted — no cash account linked';
    case 'no_expense_account':
      return 'Not posted — no expense account chosen';
    default:
      return 'Not posted';
  }
};

const resendable = (c: CheckReturn): boolean =>
  (c.status === 'issued' && c.pp_status === 'failed') ||
  c.pp_void_status === 'failed';

/**
 * The issued-check register for one association.
 *
 * Recording a check posts it to the GL and registers it with the bank's
 * Positive Pay service in one action. The bank side is issue-only — it
 * matches presented checks against what is registered here and decides
 * exceptions in Online Banking — so the two badges on each row answer two
 * different questions: is it in our books, and does the bank know.
 */
const HoaCheckRegister: React.FC<HoaCheckRegisterProps> = ({
  hoaId,
  refreshKey = 0,
  onStopPlaced,
}) => {
  const userType = useMemo(() => getUserTypeFromToken(), []);
  const canRecord = useMemo(() => canRecordCheck(userType), [userType]);
  const canStop = useMemo(() => canManageStopPayments(userType), [userType]);
  const [stopping, setStopping] = useState<CheckReturn | null>(null);

  const [checks, setChecks] = useState<CheckReturn[]>([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const [voiding, setVoiding] = useState<CheckReturn | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchChecks = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await bankService.listHoaChecks({ hoaId });
      setChecks(resp.data.checks);
    } catch (err) {
      console.error('Error fetching check register:', err);
      const { body } = getBankErrorParts(err);
      setAlert({
        type: 'error',
        title: "Couldn't load the check register",
        message: body || 'Please try again.',
      });
      setChecks([]);
    } finally {
      setLoading(false);
    }
  }, [hoaId]);

  useEffect(() => {
    fetchChecks();
  }, [fetchChecks, refreshKey]);

  // The next number a manager will most likely write: one past the
  // highest recorded. A suggestion, always editable.
  const nextCheckNumber = useMemo(() => {
    const numbers = checks
      .map((c) => Number(c.check_number))
      .filter((n) => Number.isFinite(n));
    return numbers.length ? String(Math.max(...numbers) + 1) : '';
  }, [checks]);

  // Anything the bank has accepted but not yet imported. The refresh
  // button only earns its place while this is non-zero: a settled
  // register has nothing to ask the bank about.
  const awaitingCount = useMemo(
    () =>
      checks.filter(
        (c) => c.pp_status === 'sent' || c.pp_void_status === 'sent'
      ).length,
    [checks]
  );

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setAlert(null);
      const resp = await bankService.syncHoaChecks({ hoaId });
      setChecks(resp.data.checks.checks);
      const { downloaded, awaiting } = resp.data;
      setAlert({
        type: 'success',
        title:
          downloaded > 0
            ? `${downloaded} ${downloaded === 1 ? 'entry' : 'entries'} now at the bank`
            : awaiting > 0
              ? 'Not imported yet'
              : 'Nothing waiting',
        message:
          downloaded > 0
            ? 'The bank has imported them into Positive Pay.'
            : awaiting > 0
              ? 'The bank has accepted the entries but not imported them yet. It usually takes a few minutes; try again shortly.'
              : 'Every entry is already at the bank.',
      });
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setAlert({
        type: 'error',
        title: "Couldn't refresh",
        message: body || 'Please try again.',
      });
    } finally {
      setRefreshing(false);
    }
  };

  const staleCount = useMemo(
    () => checks.filter((c) => c.download_stale).length,
    [checks]
  );

  const handleRecorded = (check: CheckReturn) => {
    fetchChecks();
    if (check.pp_status === 'failed') {
      setAlert({
        type: 'error',
        title: `Check #${check.check_number} recorded, but the bank refused it`,
        message:
          (check.pp_error_code ? `Code ${check.pp_error_code}: ` : '') +
          (check.pp_error_message || 'No detail from the bank.') +
          ' The check is in the books; use “Send again” once the bank is reachable.',
      });
    } else {
      setAlert({
        type: 'success',
        title: `Check #${check.check_number} recorded`,
        message:
          check.pp_status === 'sent' || check.pp_status === 'downloaded'
            ? 'Posted to the books and registered with the bank.'
            : 'Posted to the books. Bank registration is pending.',
      });
    }
  };

  const handleSendAgain = async (check: CheckReturn) => {
    try {
      setBusyId(check.id);
      setAlert(null);
      const resp = await bankService.sendHoaCheck({ hoaId, id: check.id });
      const after = resp.data;
      const failed =
        after.pp_status === 'failed' || after.pp_void_status === 'failed';
      setAlert(
        failed
          ? {
              type: 'error',
              title: `Check #${after.check_number} still not accepted`,
              message:
                after.pp_error_message ||
                after.pp_void_error_message ||
                'The bank refused it again.',
            }
          : {
              type: 'success',
              title: `Check #${after.check_number} registered`,
              message: 'The bank has accepted the entry.',
            }
      );
      fetchChecks();
    } catch (err) {
      const { body } = getBankErrorParts(err);
      setAlert({
        type: 'error',
        title: "Couldn't send",
        message: body || 'Please try again.',
      });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="flex flex-col space-y-4 sm:flex-row sm:items-start sm:justify-between sm:space-y-0 mb-4">
        <div>
          <Heading level={4}>Check Register</Heading>
          <Text className="mt-1 text-sm">
            Checks written from this HOA’s accounts. Recording one posts it to
            the books and registers it with the bank, so a presented check
            the bank cannot match is flagged.
          </Text>
        </div>
        {canRecord && (
          <div className="flex items-center gap-2">
            {awaitingCount > 0 && (
              <Button plain onClick={handleRefresh} disabled={refreshing}>
                <ArrowPathIcon
                  className={`h-5 w-5 mr-1 ${refreshing ? 'animate-spin' : ''}`}
                />
                {refreshing ? 'Asking the bank…' : 'Refresh from bank'}
              </Button>
            )}
            <Button color="sky" onClick={() => setIsRecordOpen(true)}>
              <PencilSquareIcon className="h-5 w-5 mr-2" />
              Record Check
            </Button>
          </div>
        )}
      </div>

      {alert && (
        <ShowAlert
          alertType={alert.type}
          alertTitle={alert.title}
          alertMessage={alert.message}
          isVisible={!!alert}
          onClose={() => setAlert(null)}
          timeout={12000}
          isInline={true}
        />
      )}

      {staleCount > 0 && (
        <div className="mb-4 rounded-md bg-amber-50 dark:bg-amber-900/30 px-4 py-3">
          <Text className="text-sm">
            <span className="font-medium">
              {staleCount} {staleCount === 1 ? 'check' : 'checks'} accepted
              by the bank but not yet imported
            </span>{' '}
            since yesterday. The bank normally imports registered checks
            within minutes; if this persists, something changed on the
            bank's side and these checks are not protected until it clears.
          </Text>
        </div>
      )}

      <div className="bg-stone-100 dark:bg-gray-600 rounded-lg shadow transition-colors duration-200 overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          {loading ? (
            <LoadingSpinner />
          ) : checks.length === 0 ? (
            <Text>
              No checks recorded yet.
              {canRecord
                ? ' Use “Record Check” after writing a check from one of this HOA’s accounts.'
                : ''}
            </Text>
          ) : (
            <div className="overflow-x-auto">
              <div className="inline-block min-w-full align-middle">
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                  <Table>
                    <TableHead className="bg-stone-100 dark:bg-gray-600">
                      <TableRow>
                        <TableHeader className="text-base font-semibold">
                          Issued
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Check #
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Payee
                        </TableHeader>
                        <TableHeader className="text-base font-semibold text-right">
                          Amount
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Account
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Books
                        </TableHeader>
                        <TableHeader className="text-base font-semibold">
                          Bank
                        </TableHeader>
                        {(canRecord || canStop) && (
                          <TableHeader className="text-base font-semibold">
                            <span className="sr-only">Actions</span>
                          </TableHeader>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {checks.map((c) => {
                        const isVoid = c.status === 'void';
                        return (
                          <TableRow key={c.id}>
                            <TableCell>{formatDate(c.issue_date)}</TableCell>
                            <TableCell className="font-medium tabular-nums">
                              {c.check_number}
                            </TableCell>
                            <TableCell>
                              <div className={isVoid ? 'line-through' : ''}>
                                {c.payee_name}
                              </div>
                              {c.memo && (
                                <Text className="text-xs">{c.memo}</Text>
                              )}
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              <span className={isVoid ? 'line-through' : ''}>
                                {formatCurrency(Number(c.amount))}
                              </span>
                            </TableCell>
                            <TableCell className="tabular-nums">
                              {c.account_number}
                            </TableCell>
                            <TableCell>
                              {isVoid ? (
                                <div className="flex flex-col gap-1">
                                  <Badge color="zinc">Void</Badge>
                                  {c.void_reason && (
                                    <Text className="text-xs">
                                      {c.void_reason}
                                    </Text>
                                  )}
                                </div>
                              ) : c.journal_entry_id ? (
                                <Badge color="lime">Posted</Badge>
                              ) : (
                                <Badge
                                  color="zinc"
                                  title={glSkipLabel(c.gl_skipped_reason)}
                                >
                                  {glSkipLabel(c.gl_skipped_reason)}
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <Badge color={ppColor(c.pp_status)}>
                                    {ppLabel(c.pp_status)}
                                  </Badge>
                                  {c.download_stale && (
                                    <Badge color="amber">Not imported</Badge>
                                  )}
                                  {c.active_stop_payment_id && (
                                    <Badge
                                      color="amber"
                                      title="The bank has been told to refuse this check"
                                    >
                                      Stopped
                                    </Badge>
                                  )}
                                </div>
                                {c.pp_status === 'failed' && (
                                  <Text className="text-xs text-red-700 dark:text-red-300">
                                    {c.pp_error_code
                                      ? `Code ${c.pp_error_code}: `
                                      : ''}
                                    {c.pp_error_message || 'Refused by the bank'}
                                  </Text>
                                )}
                                {isVoid && c.pp_void_status && (
                                  <div className="flex items-center gap-2">
                                    <Text className="text-xs">Void:</Text>
                                    <Badge color={ppColor(c.pp_void_status)}>
                                      {ppLabel(c.pp_void_status)}
                                    </Badge>
                                  </div>
                                )}
                                {c.pp_void_status === 'failed' && (
                                  <Text className="text-xs text-red-700 dark:text-red-300">
                                    {c.pp_void_error_message ||
                                      'Void refused by the bank'}
                                  </Text>
                                )}
                              </div>
                            </TableCell>
                            {(canRecord || canStop) && (
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {canRecord && resendable(c) && (
                                    <Button
                                      plain
                                      disabled={busyId === c.id}
                                      onClick={() => handleSendAgain(c)}
                                    >
                                      {busyId === c.id
                                        ? 'Sending…'
                                        : 'Send again'}
                                    </Button>
                                  )}
                                  {canRecord && !isVoid && (
                                    <Button
                                      plain
                                      disabled={busyId === c.id}
                                      onClick={() => setVoiding(c)}
                                    >
                                      Void
                                    </Button>
                                  )}
                                  {canStop && !isVoid && (
                                    <Button
                                      plain
                                      disabled={
                                        busyId === c.id ||
                                        !!c.active_stop_payment_id
                                      }
                                      title={
                                        c.active_stop_payment_id
                                          ? 'Release it from the stop payments list below'
                                          : undefined
                                      }
                                      onClick={() => setStopping(c)}
                                    >
                                      {c.active_stop_payment_id
                                        ? 'Stopped'
                                        : 'Stop payment'}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <CheckDialog
        open={isRecordOpen}
        onClose={() => setIsRecordOpen(false)}
        hoaId={hoaId}
        suggestedCheckNumber={nextCheckNumber}
        onRecorded={handleRecorded}
      />

      <StopPaymentDialog
        mode={stopping ? { check: stopping } : null}
        hoaId={hoaId}
        onClose={() => setStopping(null)}
        onPlaced={(after) => {
          setStopping(null);
          fetchChecks();
          onStopPlaced?.(after);
          setAlert(
            after.status === 'failed'
              ? {
                  type: 'error',
                  title: `Stop on check #${after.check_number} recorded, but the bank refused it`,
                  message:
                    (after.bank_error_message || 'No detail from the bank.') +
                    ' Use “Place again” in the stop payments list once the bank is reachable.',
                }
              : {
                  type: 'success',
                  title: `Stop placed on check #${after.check_number}`,
                  message:
                    'The bank will refuse it if it is presented. Void the check in the books once a replacement is written.',
                }
          );
        }}
      />
      <VoidCheckDialog
        check={voiding}
        hoaId={hoaId}
        onClose={() => setVoiding(null)}
        onVoided={(after) => {
          setVoiding(null);
          fetchChecks();
          setAlert(
            after.pp_void_status === 'failed'
              ? {
                  type: 'error',
                  title: `Check #${after.check_number} voided in the books, but the bank refused the void`,
                  message:
                    after.pp_void_error_message ||
                    'Use “Send again” once the bank is reachable.',
                }
              : {
                  type: 'success',
                  title: `Check #${after.check_number} voided`,
                  message: after.pp_void_status
                    ? 'The books are reversed and the void is registered with the bank.'
                    : 'The books are reversed. The bank had not accepted this check, so there was nothing to void there.',
                }
          );
        }}
      />
    </>
  );
};

export default HoaCheckRegister;
